import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useZustandLocation } from "@faire/retailer-visitor-shared/app/_lib/routing/useZustandLocation";
import { LazyImageGallery } from "@faire/retailer-visitor-shared/components/ImageGallery/index";
import { getAltTagProductPage } from "@faire/retailer-visitor-shared/features/Marketplace/utils";
import { useCartProductAddOptions } from "@faire/retailer-visitor-shared/features/ProductAdd/useCartProductAddOptions";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getLocaleKey } from "@faire/retailer-visitor-shared/serialized-data/getLocaleKey";
import { useProductAddToCartState } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/useProductAddToCartState";
import { getProductOptions } from "@faire/retailer-visitor-shared/views/product/utils/productUtils";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { parseQueryParams } from "@faire/web--source/common/query-params/parseQueryParams";
import { useTraceComponentRender } from "@faire/web--source/common/tracing/useTraceComponentRender";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { trackApparelDiscoveryShowroomV2View } from "@faire/web-api--source/events/apparelDiscovery/view/showroomV2";
import { trackRetailersPageViewView } from "@faire/web-api--source/events/retailers/view/pageView";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import uniqBy from "lodash/uniqBy";
import { observer } from "mobx-react";
import React, { Suspense, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useImageGalleryHandler, useTrackImageChangeProductEvent } from "@faire/retailer/views/product/__internal__/Content/hooks";
import { useProductImageOrVideoViewState } from "@faire/retailer/views/product/ProductImages/useProductImageOrVideoViewState";
import { LazyShowroomPDPEducationSection } from "@faire/retailer/views/product/ShowroomComponents/LazyShowroomPDPEducationSection";
import { ProductDescriptionAccordions } from "./ProductDescriptionAccordions";
import { ProductDetails } from "./ProductDetails/ProductDetails";
import { ImageGrid } from "./ProductImages/Components/ImageGrid";
import { getQuantityMap } from "./utils/getQuantityMap";
var ProductContentFn = function(param) {
    var productToken = param.productToken, brand = param.brand, product = param.product, className = param.className, brandReviewSummary = param.brandReviewSummary, importShipmentInfo = param.importShipmentInfo, infoTexts = param.infoTexts, showroomData = param.showroomData, disclaimers = param.disclaimers, preselectedProductOptionToken = param.preselectedProductOptionToken, badges = param.badges, productAttributeTags = param.productAttributeTags, productVideos = param.productVideos, samplesBannerDetails = param.samplesBannerDetails, loadTimingRef = param.loadTimingRef, afterAddToCart = param.afterAddToCart, showSideCart = param.showSideCart;
    var _productAddToCartState_selectedOption, _getLocaleKey, _product_details;
    useTraceComponentRender("ProductContent");
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useZustandLocation = useZustandLocation(function(param) {
        var search = param.search, pathname = param.pathname;
        return {
            search: search,
            pathname: pathname
        };
    }), search = _useZustandLocation.search, pathname = _useZustandLocation.pathname;
    var queryParams = parseQueryParams(search);
    var addOptions = useCartProductAddOptions();
    var quantityMap = useMemo(function() {
        return getQuantityMap(queryParams);
    }, [
        queryParams
    ]);
    var _brand_token;
    var productAddToCartState = useProductAddToCartState(product, (_brand_token = brand.token) !== null && _brand_token !== void 0 ? _brand_token : "", addOptions, true, quantityMap, undefined, productToken);
    var uniqueImages = React.useMemo(function() {
        var _product_visuals, _productAddToCartState_selectedOption, _productAddToCartState_selectedOption1, _product_visuals1;
        var _product_visuals_images, _productAddToCartState_selectedOption_images, _productAddToCartState_selectedOption_images1, _product_visuals_images1;
        var images = getProductOptions(product).length === 1 ? _to_consumable_array((_product_visuals_images = (_product_visuals = product.visuals) === null || _product_visuals === void 0 ? void 0 : _product_visuals.images) !== null && _product_visuals_images !== void 0 ? _product_visuals_images : []).concat(_to_consumable_array((_productAddToCartState_selectedOption_images = (_productAddToCartState_selectedOption = productAddToCartState.selectedOption) === null || _productAddToCartState_selectedOption === void 0 ? void 0 : _productAddToCartState_selectedOption.images) !== null && _productAddToCartState_selectedOption_images !== void 0 ? _productAddToCartState_selectedOption_images : [])) : _to_consumable_array((_productAddToCartState_selectedOption_images1 = (_productAddToCartState_selectedOption1 = productAddToCartState.selectedOption) === null || _productAddToCartState_selectedOption1 === void 0 ? void 0 : _productAddToCartState_selectedOption1.images) !== null && _productAddToCartState_selectedOption_images1 !== void 0 ? _productAddToCartState_selectedOption_images1 : []).concat(_to_consumable_array((_product_visuals_images1 = (_product_visuals1 = product.visuals) === null || _product_visuals1 === void 0 ? void 0 : _product_visuals1.images) !== null && _product_visuals_images1 !== void 0 ? _product_visuals_images1 : []));
        return uniqBy(images, function(image) {
            return image.url;
        });
    }, [
        product,
        (_productAddToCartState_selectedOption = productAddToCartState.selectedOption) === null || _productAddToCartState_selectedOption === void 0 ? void 0 : _productAddToCartState_selectedOption.images
    ]);
    var productImageOrVideoViewState = useProductImageOrVideoViewState(uniqueImages, productVideos);
    var setIndex = productImageOrVideoViewState.setIndex, setImages = productImageOrVideoViewState.setImages;
    // Effects
    useEffect(function() {
        setImages(uniqueImages);
    }, [
        setImages,
        uniqueImages
    ]);
    useEffect(function() {
        setIndex(0);
    }, [
        productAddToCartState.selectedOption,
        setIndex
    ]);
    var showElevatedMPPProductExperience = !!(showroomData === null || showroomData === void 0 ? void 0 : showroomData.isMidPricePointApparelProduct);
    var showVerticalImagery = (showroomData === null || showroomData === void 0 ? void 0 : showroomData.showVerticalImagery) || false;
    useEffect(function() {
        if (showElevatedMPPProductExperience) {
            var _product_token;
            trackApparelDiscoveryShowroomV2View("pdp", (_product_token = product.token) !== null && _product_token !== void 0 ? _product_token : "", showVerticalImagery ? "vertical-image" : "square-image");
        }
    }, [
        product.token,
        showVerticalImagery,
        showElevatedMPPProductExperience
    ]);
    useEffect(function() {
        var _brand_token, _product_token;
        trackRetailersPageViewView((_brand_token = brand.token) !== null && _brand_token !== void 0 ? _brand_token : "", (_product_token = product.token) !== null && _product_token !== void 0 ? _product_token : "", "", "product", pathname, isLoggedInRetailerOrBrandPreview() ? "page-product-logged-in" : "page-product-logged-out");
    }, [
        brand.token,
        product.token,
        pathname
    ]);
    // A few helpers
    var _useImageGalleryHandler = useImageGalleryHandler(), openImageGallery = _useImageGalleryHandler.openImageGallery, closeImageGallery = _useImageGalleryHandler.closeImageGallery, isImageGalleryOpen = _useImageGalleryHandler.isImageGalleryOpen;
    useTrackImageChangeProductEvent(productImageOrVideoViewState, product.token);
    var isEnglish = ((_getLocaleKey = getLocaleKey()) === null || _getLocaleKey === void 0 ? void 0 : _getLocaleKey.language) === Language.ENG;
    var selectedIndex = productImageOrVideoViewState.selectedIndex;
    var _product_token;
    return /*#__PURE__*/ _jsxs(Wrapper, {
        className: className,
        ref: loadTimingRef,
        "data-test-id": "product-content",
        children: [
            /*#__PURE__*/ _jsx(ImageWrapper, {
                children: /*#__PURE__*/ _jsx(ImageGrid, {
                    alt: getAltTagProductPage(product, isEnglish, brand.name, strictLocalize),
                    openImageGallery: openImageGallery,
                    setIndex: productImageOrVideoViewState.setIndex,
                    showVerticalImagery: showVerticalImagery,
                    productMedia: productImageOrVideoViewState.getProductMedia(),
                    showroomData: showroomData
                })
            }),
            isImageGalleryOpen ? /*#__PURE__*/ _jsx(Suspense, {
                fallback: null,
                children: /*#__PURE__*/ _jsx(LazyImageGallery, {
                    index: selectedIndex,
                    productMedia: productImageOrVideoViewState.getProductMedia(),
                    onChangeIndex: function(index) {
                        return productImageOrVideoViewState.setIndex(index);
                    },
                    onClose: closeImageGallery,
                    eventName: "product/".concat(product.token)
                })
            }) : null,
            /*#__PURE__*/ _jsx(StyledProductDetails, {
                disclaimers: disclaimers,
                brand: brand,
                product: product,
                productAddToCartState: productAddToCartState,
                brandReviewSummary: brandReviewSummary,
                importShipmentInfo: importShipmentInfo,
                infoTexts: infoTexts,
                showElevatedMPPProductExperience: showElevatedMPPProductExperience,
                preselectedProductOptionToken: preselectedProductOptionToken,
                badges: badges,
                sampleDetails: samplesBannerDetails,
                afterAddToCart: afterAddToCart,
                showSideCart: showSideCart
            }),
            showElevatedMPPProductExperience ? /*#__PURE__*/ _jsx(Suspense, {
                fallback: null,
                children: /*#__PURE__*/ _jsx(LazyShowroomPDPEducationSection, {
                    productToken: product.token
                })
            }) : null,
            /*#__PURE__*/ _jsx(StyledProductDescriptionAccordionsContainer, {
                children: /*#__PURE__*/ _jsx(ProductDescriptionAccordions, {
                    group: (_product_details = product.details) === null || _product_details === void 0 ? void 0 : _product_details.product_information_section_groups,
                    selectedOption: productAddToCartState.selectedOption,
                    productToken: (_product_token = product.token) !== null && _product_token !== void 0 ? _product_token : "",
                    productAttributeTags: productAttributeTags !== null && productAttributeTags !== void 0 ? productAttributeTags : [],
                    brand: brand,
                    samplesBannerDetails: samplesBannerDetails
                })
            })
        ]
    });
};
export var ProductContent = observer(ProductContentFn);
ProductContent.displayName = "ProductContent";
var StyledProductDescriptionAccordionsContainer = styled.div.withConfig({
    componentId: "sc-a9f3bfa-0"
})([
    "",
    "{width:100vw;}grid-area:product-description-tabs;"
], responsiveMediaQueries.mobile);
var Wrapper = styled.div.withConfig({
    componentId: "sc-a9f3bfa-1"
})([
    'display:grid;align-items:start;justify-content:center;margin:0 auto;grid-template:"product-images" "product-details" "product-description-tabs" "mpp-description" / 1fr;',
    '{grid-template:"focused-image            .               product-details " ".                        .               product-details " 16px "mpp-description          .               product-details " auto "product-description-tabs .               product-details " 1fr / 1fr 40px 300px;}',
    '{grid-template:"focused-image            .               product-details " ".                        .               product-details " 16px "mpp-description          .               product-details " auto "product-description-tabs .               product-details " 1fr / 1fr 40px 360px;}'
], responsiveMediaQueries.tabletAndAbove, responsiveMediaQueries.desktopAndAbove);
var StyledProductDetails = styled(ProductDetails).withConfig({
    componentId: "sc-a9f3bfa-2"
})([
    "grid-area:product-details;"
]);
var ImageWrapper = styled.div.withConfig({
    componentId: "sc-a9f3bfa-3"
})([
    "position:relative;grid-area:focused-image;",
    "{grid-area:product-images;}"
], responsiveMediaQueries.mobile);
